import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, User, ArrowRight, Play, CheckCircle } from 'lucide-react';
import { ResponsiveImage } from '../components/ResponsiveImage';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://motoring-daily.onrender.com';
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const FALLBACK_HERO_ARTICLES = [
  {
    id: 'fallback-main',
    title: 'Welcome To Motoringdaily',
    category: 'News',
    author: 'Motoringdaily',
    read_time: 2,
    image_url: 'https://images.unsplash.com/photo-1549921296-3a6b8a6b0f2b?auto=format&fit=crop&w=1600&q=80'
  },
  {
    id: 'fallback-side-1',
    title: 'Drive Smarter With The Latest Auto Trends',
    category: 'Technology',
    image_url: 'https://images.unsplash.com/photo-1493238792000-8113da705763?auto=format&fit=crop&w=1200&q=80'
  },
  {
    id: 'fallback-side-2',
    title: 'Electric Mobility Is Transforming City Roads',
    category: 'EV',
    image_url: 'https://images.unsplash.com/photo-1550355291-bbee04a92027?auto=format&fit=crop&w=1200&q=80'
  }
];

export const HomePage = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [email, setEmail] = useState('');
  const [newsletterError, setNewsletterError] = useState('');
  const [submittingNewsletter, setSubmittingNewsletter] = useState(false);
  const [showNewsletterSuccess, setShowNewsletterSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videoContent, setVideoContent] = useState({});
  const [newsletterContent, setNewsletterContent] = useState({});
  const navigate = useNavigate();

  const categories = ['All', 'News', 'Reviews', 'EV', 'Performance', 'Luxury', 'Technology'];
  const heroArticles = featuredArticles.length ? featuredArticles : FALLBACK_HERO_ARTICLES;

  useEffect(() => {
    console.info('[HomePage] API base URL:', API_URL);
    if (!process.env.REACT_APP_BACKEND_URL) {
      console.warn('[HomePage] REACT_APP_BACKEND_URL is undefined, using fallback Render URL.');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchArticles = async () => {
      try {
        console.info('[HomePage] Requesting articles from:', `${API_URL}/api/articles?limit=20`);
        const { data } = await axios.get(`${API_URL}/api/articles?limit=20`);
        console.info('[HomePage] Articles response count:', Array.isArray(data) ? data.length : 0);
        const featured = data.filter(a => a.featured).slice(0, 3);
        if (!isMounted) return;

        setFeaturedArticles(featured);
        setLatestArticles(data.slice(0, 12));

        // Update video content CTA link to first featured article
        if (featured.length > 0) {
          setVideoContent(prev => {
            if (prev.cta_link) return prev;
            return {
              ...prev,
              cta_link: `/article/${featured[0].id}`
            };
          });
        }
      } catch (error) {
        console.error('[HomePage] Error fetching articles:', error?.response?.data || error.message || error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const fetchHomepageContent = async () => {
      try {
        console.info('[HomePage] Requesting homepage content from:', `${API_URL}/api/homepage-content/video_showcase`, `${API_URL}/api/homepage-content/newsletter`);
        const videoRes = await axios.get(`${API_URL}/api/homepage-content/video_showcase`);
        const newsletterRes = await axios.get(`${API_URL}/api/homepage-content/newsletter`);
        console.info('[HomePage] Homepage content response:', {
          video: !!videoRes?.data,
          newsletter: !!newsletterRes?.data
        });

        // If CTA link is /reviews or empty, use first featured article
        const videoData = videoRes.data;
        if (!videoData.cta_link || videoData.cta_link === '/reviews') {
          const { data: articles } = await axios.get(`${API_URL}/api/articles?featured=true&limit=1`);
          if (articles.length > 0) {
            videoData.cta_link = `/article/${articles[0].id}`;
          }
        }

        if (!isMounted) return;
        setVideoContent(videoData);
        setNewsletterContent(newsletterRes.data);
      } catch (error) {
        console.error('[HomePage] Error fetching homepage content:', error?.response?.data || error.message || error);
      }
    };

    fetchArticles();
    fetchHomepageContent();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setNewsletterError('Please enter a valid email address');
      return;
    }

    setNewsletterError('');
    setSubmittingNewsletter(true);

    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email: normalizedEmail });
      setShowNewsletterSuccess(true);
      setEmail('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'This email is already subscribed') {
        setNewsletterError('This email is already subscribed');
      } else if (detail === 'Invalid email address') {
        setNewsletterError('Invalid email address');
      } else {
        setNewsletterError(detail || 'Failed to subscribe');
      }
    } finally {
      setSubmittingNewsletter(false);
    }
  };

  const filteredArticles = selectedCategory === 'All'
    ? latestArticles
    : latestArticles.filter(a => a.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Featured Article */}
            {heroArticles[0] && (
              <Link
                to={heroArticles[0].id.startsWith('fallback-') ? '/news' : `/article/${heroArticles[0].id}`}
                className="col-span-1 lg:col-span-8 relative h-[400px] lg:h-[600px] group overflow-hidden"
                data-testid="hero-featured-article"
              >
                <ResponsiveImage
                  src={heroArticles[0].image_url}
                  alt={heroArticles[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  width={1600}
                  quality={78}
                  eager
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <span className="bg-[#00A859] text-white text-xs font-bold uppercase tracking-wider py-1 px-3 inline-block mb-4">
                    {heroArticles[0].category}
                  </span>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black text-white mb-4 font-heading">
                    {heroArticles[0].title}
                  </h2>
                  <div className="flex items-center space-x-4 text-white/80 text-sm">
                    <span className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {heroArticles[0].author}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {heroArticles[0].read_time} min read
                    </span>
                  </div>
                </div>
              </Link>
            )}

            {/* Side Featured Articles */}
            <div className="col-span-1 lg:col-span-4 flex flex-col gap-6">
              {heroArticles.slice(1, 3).map((article) => (
                <Link
                  key={article.id}
                  to={article.id.startsWith('fallback-') ? '/news' : `/article/${article.id}`}
                  className="relative h-[190px] lg:h-[294px] group overflow-hidden"
                  data-testid="hero-side-article"
                >
                  <ResponsiveImage
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    width={900}
                    quality={72}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="bg-[#00A859] text-white text-xs font-bold uppercase tracking-wider py-1 px-3 inline-block mb-2">
                      {article.category}
                    </span>
                    <h3 className="text-xl font-bold text-white font-heading">
                      {article.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles Section */}
      <section className="py-16 sm:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-8 font-heading">
            Explore Our Latest Articles
          </h2>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`py-2 px-4 rounded-none text-sm font-semibold uppercase tracking-wider transition-all ${
                  selectedCategory === category
                    ? 'bg-[#00A859] text-white'
                    : 'bg-white text-[#1A1A1A] hover:bg-[#00A859] hover:text-white border border-[#E5E7EB]'
                }`}
                data-testid={`category-tab-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                data-testid="article-card"
              >
                <div className="relative h-48 overflow-hidden">
                  <ResponsiveImage
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    width={800}
                    quality={70}
                  />
                  <span className="absolute top-4 left-4 bg-[#00A859] text-white text-xs font-bold uppercase tracking-wider py-1 px-3">
                    {article.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold tracking-tight mb-2 font-heading">
                    {article.title}
                  </h3>
                  <p className="text-base leading-relaxed text-[#666666] mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-[#666666]">
                    <span>{article.author}</span>
                    <span className="flex items-center text-[#00A859] font-bold uppercase tracking-wider">
                      Read More <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Video Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div
              className="relative h-[300px] lg:h-[400px] group cursor-pointer"
              onClick={() => {
                if (!videoContent.video_url) return;
                window.open(videoContent.video_url.replace('embed/', 'watch?v='), '_blank');
              }}
            >
              <ResponsiveImage
                src={videoContent.image_url || "https://images.unsplash.com/photo-1769608979776-091a9651e20c"}
                alt="Featured Video"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                width={1200}
                quality={74}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-[#00A859] hover:bg-[#008a49] w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center transition-all duration-300 transform group-hover:scale-110 animate-pulse">
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" />
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold font-heading animate-fade-in">
                {videoContent.title || "Premium Auto Showcase"}
              </h2>
              <p className="text-lg leading-relaxed text-[#1A1A1A] animate-fade-in-delay">
                {videoContent.description || "Discover this week's featured vehicle — a masterpiece of engineering and design."}
              </p>
              <button 
                onClick={() => navigate(videoContent.cta_link || '/reviews')}
                className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-3 px-8 rounded-none transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-95"
              >
                {videoContent.cta_text || "Find Out More"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 sm:py-24 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 font-heading animate-fade-in">
            {newsletterContent.title || "Get Our Latest News & Update"}
          </h2>
          <p className="text-lg leading-relaxed text-[#666666] mb-8 animate-fade-in-delay">
            {newsletterContent.description || "Subscribe to our newsletter and stay updated with the latest automotive news."}
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-2">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (newsletterError) setNewsletterError('');
              }}
              placeholder="Enter your email"
              required
              className="bg-white border border-[#E5E7EB] focus:border-[#00A859] focus:ring-2 focus:ring-[#00A859] rounded-none px-4 py-3 text-[#1A1A1A] w-full sm:w-96 transition-all duration-300"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              disabled={submittingNewsletter}
              className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-3 px-8 rounded-none transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-95"
              data-testid="newsletter-submit-btn"
            >
              {submittingNewsletter ? 'Submitting...' : (newsletterContent.cta_text || "Submit Now")}
            </button>
          </form>
          {newsletterError && (
            <p className="text-red-500 text-sm mt-3">{newsletterError}</p>
          )}
        </div>
      </section>

      {showNewsletterSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00A859] text-white rounded-full mb-4">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold mb-2 font-heading text-[#1A1A1A]">Success!</h3>
            <p className="text-[#666666] mb-6">Subscribed successfully!</p>
            <button
              onClick={() => setShowNewsletterSuccess(false)}
              className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};